import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconGenerate,
  IconHistory,
  IconResources,
  IconSearch,
  IconTags,
  IconSettings,
} from './icons';

const NAV_ITEMS = [
  { path: '/', labelKey: 'common.generate', Icon: IconGenerate },
  { path: '/history', labelKey: 'common.history', Icon: IconHistory },
  { path: '/resources', labelKey: 'common.resources', Icon: IconResources },
  { path: '/search', labelKey: 'common.search', Icon: IconSearch },
  { path: '/tags', labelKey: 'common.tags', Icon: IconTags },
  { path: '/settings', labelKey: 'common.settings', Icon: IconSettings },
] as const;

export function Navigation() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number } | null>(null);

  // Position the sliding indicator under the active item
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const activeButton = nav.querySelector('[data-active="true"]') as HTMLElement | null;
    if (activeButton) {
      setIndicatorStyle({
        left: activeButton.offsetLeft,
        width: activeButton.offsetWidth,
      });
    }
  }, [location.pathname]);

  return (
    <nav
      ref={navRef}
      className="fixed bottom-0 left-0 right-0 z-50 bg-tg-bg"
      style={{
        borderTop: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 15%, transparent)',
      }}
    >
      {/* Sliding active indicator */}
      {indicatorStyle !== null && (
        <div
          className="absolute top-0 h-0.5 rounded-full bg-tg-button transition-all duration-300 ease-out"
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
        />
      )}

      <div className="flex items-center justify-around px-1 pt-1.5 pb-1">
        {NAV_ITEMS.map(({ path, labelKey, Icon }) => {
          const isActive = location.pathname === path;
          return (
            <button
              key={path}
              type="button"
              data-active={isActive}
              onClick={() => { navigate(path); }}
              className="group relative flex flex-col items-center gap-0.5 px-1 py-1 min-w-0 flex-1 rounded-lg transition-all duration-150 active:scale-95"
              style={{
                color: isActive
                  ? 'var(--tg-theme-button-color)'
                  : 'var(--tg-theme-hint-color)',
              }}
            >
              <span
                className="text-base leading-none transition-all duration-200"
                style={{
                  transform: isActive ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                <Icon size={20} />
              </span>
              <span
                className="text-[10px] leading-tight truncate w-full text-center transition-all duration-150"
                style={{
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {t(labelKey)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
